<template>
  <b-card>
    <validation-observer ref="corporateInformationRules">
      <b-form @submit.prevent="saveCorporateInformation">
        <b-row class="mt-2">
          <b-col md="6">
            <InputField
              id="bank-registered-name"
              v-model="corporateInformationData.config.registeredName"
              :label="$t('Bank\'s Registered Name')"
              :placeholder="$t('Enter bank registered name')"
              type="text"
              :error-name="$t('Bank\'s Registered Name')"
              rules="required"
              for="bank-registered-name"
              :readonly="isReadonly"
              onpaste="return false;"
              :is-allow-special-characters="false"
            />
          </b-col>

          <b-col md="6">
            <InputField
              id="bank-primary-email-address"
              v-model="corporateInformationData.config.primaryEmailAddress"
              :label="$t('Bank\'s Primary Email Address')"
              :placeholder="$t('Enter bank\'s primary email address')"
              type="email"
              :error-name="$t('Bank\'s Primary Email Address')"
              rules="required|email"
              for="bank-primary-email-address"
              :readonly="isReadonly"
            />
          </b-col>

          <b-col md="6">
            <InputField
              id="Bank's RC Number"
              v-model="corporateInformationData.config.rcNumber"
              :label="$t('Bank\'s RC Number')"
              :placeholder="$t('Enter bank rc number')"
              type="text"
              :error-name="$t('Bank\'s RC Number')"
              rules="required"
              for="Bank's RC Number"
              :readonly="isReadonly"
            />
          </b-col>

          <b-col md="6">
            <InputField
              id="bank-institution-code"
              v-model="corporateInformationData.config.institutionCode"
              :label="$t('Bank\'s Institution/CBN Code')"
              :placeholder="$t('Bank\'s Institution/CBN Code')"
              type="text"
              for="number-of-existing-branches"
              :readonly="isReadonly"
            />
          </b-col>

          <b-col md="6">
            <SelectField
              id="country-of-operation"
              v-model="corporateInformationData.config.countryOfOperation"
              :label="$t('Country of Operation')"
              :reduce="option => option"
              :options="countryOptions"
              :placeholder="$t('Select')"
              :error-name="$t('Country of Operation')"
              rules="required"
              item="label"
              :readonly="isReadonly"
            />
          </b-col>

          <b-col md="6">
            <SelectField
              id="registered-hq-location"
              v-model="corporateInformationData.config.registeredHQLocation"
              :label="$t('Bank\'s Registered HQ Location')"
              :options="headquarterOptions"
              :reduce="option => option"
              :placeholder="$t('Select')"
              :error-name="$t('Bank\'s Registered HQ Location')"
              rules="required"
              item="label"
              :readonly="isReadonly"
            />
          </b-col>

          <b-col md="6">
            <InputNumberField
              id="authorized-share-capital"
              v-model="corporateInformationData.config.authorizedShareCapital"
              :label="$t('Authorized Share Capital')"
              :error-name="$t('Authorized Share Capital')"
              rules="required|integer|positive"
              :readonly="isReadonly"
            />

          </b-col>
          <b-col md="6">
            <DatePickerField
              id="last-financial-year-end"
              v-model="corporateInformationData.config.lastFinacialYearEnd"
              rules="required"
              :label="$t('Last Financial Year End')"
              :error-name="$t('Last Financial Year End')"
              :config="lastFinacialYearEndConfig"
              :disabled="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <DatePickerField
              id="date-of-last-examination"
              v-model="corporateInformationData.config.lastRegulatoryExamination"
              rules="required"
              :label="$t('Date of Last CBN/NDIC Examination')"
              :error-name="$t('Date of Last CBN/NDIC Examination')"
              :config="lastRegulatoryExaminationConfig"
              :disabled="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="recommended-provision"
              v-model="corporateInformationData.config.recommendedProvisionOnLastExamination"
              :label="$t('Recommended Provision as at the Last Examination')"
              :placeholder="$t('Enter recommended Provision as at the Last Examinations')"
              type="number"
              :error-name="$t('Recommended Provision as at the Last Examination')"
              rules="required|integer"
              for="recommended-provision"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="number-of-existing-branches"
              v-model.number="corporateInformationData.config.numberOfExistingOperatingBranches"
              :label="$t('Number of Existing Branches in Operation')"
              :placeholder="$t('Enter number of existing branches in operation')"
              type="number"
              :error-name="$t('Number of Existing Branches in Operation')"
              rules="required|integer|positive"
              for="number-of-existing-branches"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="number-of-new-branches"
              v-model.number="corporateInformationData.config.numberOfNewBranches"
              :label="$t('Number of New Branches')"
              :placeholder="$t('Enter number of new branches')"
              type="number"
              :error-name="$t('Number of New Branches')"
              rules="required|integer|positive"
              for="number-of-new-branches"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="number-of-closed-branches"
              v-model.number="corporateInformationData.config.numberOfClosedBranches"
              :label="$t('Number of Closed Branches')"
              :placeholder="$t('Enter number of closed branches')"
              type="number"
              :error-name="$t('Number of Closed Branches')"
              rules="required|integer|positive"
              for="number-of-closed-branches"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="number-of-cash-centres"
              v-model.number="corporateInformationData.config.numberOfCashCentres"
              :label="$t('Number of Cash Centres')"
              :placeholder="$t('Enter number of cash centres')"
              type="number"
              :error-name="$t('Number of Cash Centres')"
              rules="required|integer|positive"
              for="number-of-cash-centres"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="number-of-meeting-points"
              v-model.number="corporateInformationData.config.numberOfMeetingPoints"
              :label="$t('Number of Meeting Points')"
              :placeholder="$t('Enter number of meeting points')"
              type="number"
              :error-name="$t('Number of Meeting Points')"
              rules="required|integer|positive"
              for="number-of-meeting-points"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="state-name"
              v-model="corporateInformationData.config.stateName"
              :label="$t('State Name')"
              :placeholder="$t('State Name')"
              type="text"
              :error-name="$t('State Name')"
              rules="required"
              for="state-name"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="state-code"
              v-model="corporateInformationData.config.stateCode"
              :label="$t('State Code')"
              :placeholder="$t('State Code')"
              type="text"
              :error-name="$t('State Code')"
              rules="required"
              for="state-code"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="local-governement-name"
              v-model="corporateInformationData.config.localGovernmentName"
              :label="$t('Local Governement Name')"
              :placeholder="$t('Local Governement Name')"
              type="text"
              :error-name="$t('Local Governement Name')"
              rules="required"
              for="local-governement-name"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="local-governement-code"
              v-model="corporateInformationData.config.localGovernmentCode"
              :label="$t('Local Governement Code')"
              :placeholder="$t('Local Governement Code')"
              type="text"
              :error-name="$t('Local Governement Code')"
              rules="required"
              for="local-governement-code"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="md-name"
              v-model="corporateInformationData.config.mdName"
              :label="$t('MD/CEO\'s Name')"
              :placeholder="$t('MD/CEO\'s Name')"
              type="text"
              :error-name="$t('MD/CEO\'s Name')"
              rules="required"
              for="md-name"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="md-phone-number"
              v-model="corporateInformationData.config.mdPhoneNumber"
              :label="$t('MD/CEO\'s Phone Number')"
              :placeholder="$t('MD/CEO\'s Phone Number')"
              type="text"
              :error-name="$t('MD/CEO\'s Phone Number')"
              rules="required"
              for="md-phone-number"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="md-email"
              v-model="corporateInformationData.config.mdEmail"
              :label="$t('MD/CEO\'s Email')"
              :placeholder="$t('MD/CEO\'s Email')"
              type="email"
              :error-name="$t('MD/CEO\'s Email')"
              rules="required"
              for="md-email"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="compliance-office-name"
              v-model="corporateInformationData.config.complianceOfficerName"
              :label="$t('Compliance Officer\'s Name')"
              :placeholder="$t('Compliance Officer\'s Name')"
              type="text"
              :error-name="$t('Compliance Officer\'s Name')"
              rules="required"
              for="compliance-office-name"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="compliance-phone-number"
              v-model="corporateInformationData.config.complianceOfficerNumber"
              :label="$t('Compliance Officer\'s Phone Number')"
              :placeholder="$t('Compliance Officer\'s Phone Number')"
              type="text"
              :error-name="$t('Compliance Officer\'s Phone Number')"
              rules="required"
              for="compliance-phone-number"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="compliance-email"
              v-model="corporateInformationData.config.complianceOfficerEmail"
              :label="$t('Compliance Officer\'s Email')"
              :placeholder="$t('Compliance Officer\'s Email')"
              type="email"
              :error-name="$t('Compliance Officer\'s Email')"
              rules="required"
              for="compliance-email"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="chief-finance-officer-name"
              v-model="corporateInformationData.config.chiefFinanceOfficerName"
              :label="$t('Chief Finance Officer\'s Name')"
              :placeholder="$t('Chief Finance Officer\'s Name')"
              type="text"
              :error-name="$t('Chief Finance Officer\'s Name')"
              rules="required"
              for="chief-finance-officer-name"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="chief-finance-officer-phone-number"
              v-model="corporateInformationData.config.chiefFinanceOfficerNumber"
              :label="$t('Chief Finance Officer\'s Phone Number')"
              :placeholder="$t('Chief Finance Officer\'s Phone Number')"
              type="text"
              :error-name="$t('Chief Finance Officer\'s Phone Number')"
              rules="required"
              for="chief-finance-officer-phone-number"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="chief-finance-officer-email"
              v-model="corporateInformationData.config.chiefFinanceOfficerEmail"
              :label="$t('Chief Finance Officer\'s Email')"
              :placeholder="$t('Chief Finance Officer\'s Email')"
              type="email"
              :error-name="$t('Chief Finance Officer\'s Email')"
              rules="required"
              for="chief-finance-officer-email"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="12">
            <TextAreaField
              id="bank-registered-address"
              v-model="corporateInformationData.config.registeredAddress"
              :label="$t('Bank\'s Registered Address')"
              :placeholder="$t('Enter bank\'s registered address')"
              :error-name="$t('Bank\'s Registered Address')"
              rules="required"
              for="bank-registered-address"
              rows="5"
              :readonly="isReadonly"
            />
          </b-col>

        </b-row>

        <div class="text-center">
          <Button
            v-if="getUserPermission('MODIFY_SYSTEM_SETUP')"
            :label="buttonLabel"
            :loading="isLoading"
            :disabled="isDisabled"
            size="lg"
            @click="saveCorporateInformation"
          />
        </div>
      </b-form>
    </validation-observer>
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BRow, BCol, BCard, BForm,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver } from 'vee-validate'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { required, positive } from '@validations'
import { permissionMixin } from '@core/mixins/permission'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../../forms/InputField.vue'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import TextAreaField from '../../../../forms/TextAreaField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import Button from '../../../../components/button/Button.vue'
import { toYearWithDate, toDateWithYear } from '../../../../../helpers/formatDate'
import { operationPermissions } from '../../../../../helpers/permissions'

export default {
  components: {
    Button,
    BRow,
    BCol,
    BCard,
    InputField,
    BForm,
    DatePickerField,
    InputNumberField,
    TextAreaField,
    SelectField,
    FullScreenSpinner,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  mixins: [sweetAlert, permissionMixin],
  data() {
    return {
      required,
      positive,
      isDisabled: false,
      isReadonly: true,
      isEditable: true,
      isLoading: false,
      showSpinner: false,
      buttonLabel: this.$t('Edit'),
      toYearWithDate,
      corporateInformationData: {
        config: {
          registeredName: '',
          registeredAddress: '',
          registeredHQLocation: 'Nigeria',
          primaryEmailAddress: '',
          institutionCode: '',
          rcNumber: null,
          authorizedShareCapital: null,
          lastFinacialYearEnd: null,
          lastRegulatoryExamination: null,
          recommendedProvisionOnLastExamination: '',
          numberOfExistingOperatingBranches: null,
          numberOfNewBranches: null,
          numberOfClosedBranches: null,
          numberOfCashCentres: null,
          numberOfMeetingPoints: null,
          stateName: '',
          stateCode: '',
          localGovernmentName: '',
          localGovernmentCode: '',
          mdName: '',
          mdPhoneNumber: '',
          mdEmail: '',
          complianceOfficerName: '',
          complianceOfficerNumber: '',
          complianceOfficerEmail: '',
          chiefFinanceOfficerName: '',
          chiefFinanceOfficerNumber: '',
          chiefFinanceOfficerEmail: '',
          countryOfOperation: 'Nigeria',
        },
      },
      headquarterOptions: ['Nigeria'],
      countryOptions: ['Nigeria'],
      currencyOptions: [],
      lastFinacialYearEndConfig: {
        dateFormat: 'd/m/Y',
        defaultDate: 'today',
      },
      lastRegulatoryExaminationConfig: {
        dateFormat: 'd/m/Y',
        defaultDate: 'today',
      },
    }
  },
  mounted() {
    this.fetchCorporateInformation(0)
  },
  methods: {
    ...mapActions({
      getGeneralCorporate: 'operations/getCorporateInformationList',
      updateGeneralCorporate: 'operations/updateCorporateInformationAction',
    }),
    saveCorporateInformation() {
      if (this.buttonLabel === 'Save Changes') {
        this.$refs.corporateInformationRules.validate().then(success => {
          if (success) {
            this.confirm(this.handleSubmit, { text: this.$t('Are you sure you want to Update this Corporate Information?') })
          }
        })
      } else {
        this.isReadonly = false
        this.buttonLabel = this.$t('Save Changes')
      }
    },
    async handleSubmit() {
      try {
        this.isLoading = true
        // this.lastFinacialYearEndConfig.defaultDate = this.corporateInformationData.config.lastFinacialYearEnd
        // this.lastRegulatoryExaminationConfig.defaultDate = this.corporateInformationData.config.lastRegulatoryExamination
        const response = await this.updateGeneralCorporate({
          config: {
            ...this.corporateInformationData.config,
            lastFinacialYearEnd: this.toYearWithDate(this.corporateInformationData.config.lastFinacialYearEnd),
            lastRegulatoryExamination: this.toYearWithDate(this.corporateInformationData.config.lastRegulatoryExamination),
          },
        })

        const { status } = await response
        if (status === 200) {
          this.fetchCorporateInformation()
          this.alertSuccess({ text: this.$t('Your request to Update Corporate Information was successful') })
          this.isReadonly = true
          this.isEditable = true
          this.isDisabled = false
          this.isLoading = false
          this.buttonLabel = 'Edit'
        }
      } catch (error) {
        const reasonText = this.$t('Your request to Update Corporate Information failed. Reason:')
        this.alertError({ text: `${reasonText} ${error}` })
      } finally {
        this.isEditable = true
        this.isDisabled = false
        this.isLoading = false
      }
    },

    async fetchCorporateInformation() {
      try {
        this.showSpinner = true
        const response = await this.getGeneralCorporate({ permission: operationPermissions.VIEW_SYSTEM_SETUP })
        const { status, data } = response

        if (status === 200 && data.data) {
          this.corporateInformationData.config = {
            ...data.data,
            lastFinacialYearEnd: toDateWithYear(data.data.lastFinacialYearEnd),
            lastRegulatoryExamination: toDateWithYear(data.data.lastRegulatoryExamination),
          }
          this.lastFinacialYearEndConfig.defaultDate = toDateWithYear(data.data.lastFinacialYearEnd)
          this.lastRegulatoryExaminationConfig.defaultDate = toDateWithYear(data.data.lastRegulatoryExamination)
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },

  },
}
</script>
<style scoped>
.custom-checkbox:last-child{
    margin-left: 20px !important;
}
</style>
